import type {
  ClinicConversation,
  ClinicConversationDetail,
  ClinicConversationMutation,
  ClinicMessageDirectory,
  ClinicMessageRecipient,
  ClinicMessageSource,
  CreateClinicConversation,
} from "@emr/contracts";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createAgentClient } from "../api/agentClient";
import { ClinicMessagingError } from "../api/clinicConversations";
import { useClinicalRuntime } from "../auth/ClinicalRuntimeProvider";
import { useWorkforceAccount } from "../lib/workforceAccount";
import { ClinicMessageWindow } from "./ClinicMessageWindow";
import {
  getMessageDraft,
  type MessageDraft,
  type MessageSeed,
  messageDraftKey,
  removeMessageDraft,
  saveMessageDraft,
} from "./drafts";
import "../pages/document-inbox.css";
import "./messaging.css";

export type MessageClient = ReturnType<typeof createAgentClient>;
const StoredPdfPreview = lazy(() =>
  import("../components/StoredPdfPreview").then((module) => ({ default: module.StoredPdfPreview })),
);
type Messaging = {
  enabled: boolean;
  directory?: ClinicMessageDirectory;
  client?: MessageClient;
  siteId: string;
  userId: string;
  scope: string;
  unread: number;
  connectionError: boolean;
  canAdmin: boolean;
  retry(): void;
  compose(seed?: MessageSeed): void;
  open(id: string): void;
};
export const ClinicMessagingContext = createContext<Messaging>({
  enabled: false,
  siteId: "",
  userId: "",
  scope: "",
  unread: 0,
  connectionError: false,
  canAdmin: false,
  retry() {},
  compose() {},
  open() {},
});
export const useClinicMessaging = () => useContext(ClinicMessagingContext);
const messageError = (error: unknown) =>
  error instanceof Error ? error.message : "Messaging is unavailable. Your draft has been kept.";
export const refreshMessages = (cache: ReturnType<typeof useQueryClient>) => {
  for (const key of [
    "clinic-messages",
    "clinic-message",
    "clinic-message-summary",
    "patient-messages",
    "staff-requests",
    "clinic-message-directory",
  ])
    void cache.invalidateQueries({ queryKey: [key] });
};

/** Reuses the same viewer from a clinical record without subscribing chart readers. */
export function SourceConversationLinks({
  patientId,
  kind,
  sourceId,
}: {
  patientId: string;
  kind: ClinicMessageSource["kind"];
  sourceId: string;
}) {
  const messaging = useClinicMessaging();
  return messaging.enabled ? (
    <SourceConversationList patientId={patientId} kind={kind} sourceId={sourceId} />
  ) : null;
}

function SourceConversationList({
  patientId,
  kind,
  sourceId,
}: {
  patientId: string;
  kind: ClinicMessageSource["kind"];
  sourceId: string;
}) {
  const messaging = useClinicMessaging();
  const list = useInfiniteQuery({
    queryKey: ["patient-messages", messaging.scope, patientId, kind, sourceId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      messaging.client!.listClinicConversations(
        {
          siteId: messaging.siteId,
          scope: "patient",
          patientId,
          sourceKind: kind,
          sourceId,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
        signal,
      ),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: messaging.enabled && Boolean(messaging.client),
    refetchInterval: 5000,
    refetchOnWindowFocus: "always",
    retry: false,
  });
  if (!messaging.enabled) return null;
  const items = [
    ...new Map(
      (list.data?.pages.flatMap((page) => page.items) ?? []).map((item) => [item.id, item]),
    ).values(),
  ];
  return (
    <div className="cm-source-conversations">
      {list.isError && <p role="status">Messages about this record could not be refreshed.</p>}
      {items.map((item) => (
        <button
          className="di-button"
          key={item.id}
          type="button"
          onClick={() => messaging.open(item.id)}
        >
          Message · {item.subject}
        </button>
      ))}
      {list.hasNextPage && (
        <button
          className="di-button"
          type="button"
          disabled={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          More messages about this record
        </button>
      )}
    </div>
  );
}

export function ClinicMessagingProvider({ children }: { children: ReactNode }) {
  const runtime = useClinicalRuntime();
  const account = useWorkforceAccount();
  const cache = useQueryClient();
  const siteId = runtime.config.siteId;
  const userId = account.data?.userId ?? "";
  const scope = `${account.data?.tenantId ?? ""}:${siteId}:${userId}`;
  const client = useMemo(
    () =>
      createAgentClient({
        baseUrl: runtime.config.agentApiOrigin,
        getAccessToken: runtime.workforce.accessToken,
        siteId,
      }),
    [runtime.config.agentApiOrigin, runtime.workforce.accessToken, siteId],
  );
  const permitted = [
    "physician",
    "nurse_ma",
    "front_desk",
    "referral_coordinator",
    "clinic_admin",
  ].includes(account.data?.role ?? "");
  const directory = useQuery({
    queryKey: ["clinic-message-directory", scope],
    queryFn: ({ signal }) => client.getClinicMessageDirectory(siteId, signal),
    enabled: permitted,
    refetchInterval: 15_000,
    refetchOnWindowFocus: "always",
    retry: false,
  });
  const summary = useQuery({
    queryKey: ["clinic-message-summary", scope],
    queryFn: ({ signal }) => client.listClinicConversations({ siteId, limit: 1 }, signal),
    enabled: directory.data?.enabled === true,
    refetchInterval: 15_000,
    refetchOnWindowFocus: "always",
    retry: false,
  });
  const [surface, setSurface] = useState<
    { scope: string; seed: MessageSeed } | { scope: string; id: string } | null
  >(null);
  const compose = useCallback((seed: MessageSeed = {}) => setSurface({ scope, seed }), [scope]);
  const open = useCallback((id: string) => setSurface({ scope, id }), [scope]);
  const retry = useCallback(() => {
    void account.refetch();
    refreshMessages(cache);
  }, [account.refetch, cache]);
  const value: Messaging = {
    enabled: directory.data?.enabled === true,
    ...(directory.data ? { directory: directory.data } : {}),
    client,
    siteId,
    userId,
    scope,
    unread: summary.data?.counts.unread ?? 0,
    connectionError:
      account.isError || directory.isError || (directory.data?.enabled === true && summary.isError),
    canAdmin: account.data?.role === "clinic_admin",
    retry,
    compose,
    open,
  };
  return (
    <ClinicMessagingContext.Provider value={value}>
      {children}
      {surface?.scope === scope &&
        directory.data?.enabled &&
        ("seed" in surface ? (
          <ClinicMessageComposer
            key={messageDraftKey(scope, surface.seed)}
            seed={surface.seed}
            onClose={() => setSurface((current) => (current === surface ? null : current))}
            onSent={(id) =>
              setSurface((current) => (current === surface ? { scope, id } : current))
            }
          />
        ) : (
          <ClinicConversationPanel
            key={surface.id}
            id={surface.id}
            onClose={() => setSurface((current) => (current === surface ? null : current))}
          />
        ))}
    </ClinicMessagingContext.Provider>
  );
}

export function MessageRecipients({
  directory,
  value,
  onChange,
  disabled = false,
}: {
  directory: ClinicMessageDirectory;
  value: ClinicMessageRecipient[];
  onChange(value: ClinicMessageRecipient[]): void;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState("");
  const groups = [
    {
      name: "People",
      kind: "user" as const,
      items: directory.people.map((p) => ({
        id: p.userId,
        name: p.displayName,
        detail: p.role.replaceAll("_", " "),
      })),
    },
    {
      name: "Teams",
      kind: "team" as const,
      items: directory.teams
        .filter((t) => t.active && t.members.length > 0)
        .map((t) => ({ id: t.id, name: t.name, detail: `${t.members.length} members` })),
    },
  ];
  return (
    <fieldset className="cm-recipients" disabled={disabled}>
      <legend>To · People and teams</legend>
      <input
        aria-label="Search recipients"
        placeholder="Search people or teams"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="cm-recipient-list">
        {groups.map((g) => (
          <div key={g.kind}>
            <strong>{g.name}</strong>
            {g.items
              .filter((p) => p.name.toLowerCase().includes(search.toLowerCase()))
              .map((p) => {
                const selected = value.some((r) => r.kind === g.kind && r.id === p.id);
                return (
                  <label key={p.id}>
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() =>
                        onChange(
                          selected
                            ? value.filter((r) => !(r.kind === g.kind && r.id === p.id))
                            : [...value, { kind: g.kind, id: p.id }],
                        )
                      }
                    />
                    <span>
                      {p.name}
                      <small>{p.detail}</small>
                    </span>
                  </label>
                );
              })}
          </div>
        ))}
      </div>
      <small>{value.length} selected</small>
    </fieldset>
  );
}

function useDraft(key: string, seed?: MessageSeed) {
  const [draft, setDraft] = useState(() => getMessageDraft(key, seed));
  const change = (patch: Partial<MessageDraft>) => {
    // A request may settle after its floating window closes or is replaced.
    // Persist independently of React so its saved retry state settles too.
    const next = { ...getMessageDraft(key, seed), ...patch };
    saveMessageDraft(key, next);
    setDraft(next);
  };
  return { draft, change };
}

export function ClinicMessageComposer({
  seed,
  onClose,
  onSent,
}: {
  seed: MessageSeed;
  onClose(): void;
  onSent?(id: string): void;
}) {
  const messaging = useClinicMessaging();
  const { client, directory, siteId, scope } = messaging;
  const cache = useQueryClient();
  const key = messageDraftKey(scope, seed);
  const { draft, change } = useDraft(key, seed);
  const [patientSearch, setPatientSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  const [error, setError] = useState("");
  const patients = useQuery({
    queryKey: ["message-patient-search", scope, patientSearch],
    queryFn: ({ signal }) => client!.listPatientDirectory(siteId, patientSearch, 20, signal),
    enabled: Boolean(client) && !draft.patient && patientSearch.trim().length >= 2,
    retry: false,
  });
  const patientIdentity = useQuery({
    queryKey: ["message-patient-identity", scope, draft.patient?.id],
    queryFn: ({ signal }) => client!.getPatientProfile(siteId, draft.patient!.id, signal),
    enabled: Boolean(client && draft.patient) && draft.patient?.name === "Patient chart",
    retry: false,
  });
  const demographics = patientIdentity.data?.demographics;
  if (!client || !directory) return null;
  const send = async () => {
    if (sending.current) return;
    const payload: CreateClinicConversation = (draft.pending as CreateClinicConversation) ?? {
      siteId,
      patientId: draft.patient?.id ?? null,
      subject: draft.subject,
      body: draft.body,
      recipients: draft.recipients,
      sources: draft.sources,
      idempotencyKey: crypto.randomUUID(),
      ...(draft.task && draft.patient
        ? {
            request: {
              ...seed.request,
              assignedTo: draft.owner || null,
              dueAt: draft.due ? new Date(draft.due).toISOString() : null,
            },
          }
        : {}),
    };
    sending.current = true;
    setBusy(true);
    setError("");
    change({ pending: payload });
    try {
      const result = await client.createClinicConversation(payload);
      removeMessageDraft(key);
      refreshMessages(cache);
      (onSent ?? messaging.open)(result.conversation.id);
    } catch (cause) {
      if (cause instanceof ClinicMessagingError && cause.definitive) change({ pending: undefined });
      setError(messageError(cause));
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };
  const locked = busy || Boolean(draft.pending);
  return (
    <ClinicMessageWindow
      title={draft.task ? "Alert staff" : "New message"}
      eyebrow="Current clinic · Attention"
      className="cm-dialog di-dialog"
      onClose={onClose}
      actions={
        <>
          <button type="button" className="di-button" onClick={onClose}>
            Close · keep draft
          </button>
          <button
            type="button"
            className="di-button di-primary"
            disabled={busy || (!draft.pending && (!draft.body.trim() || !draft.recipients.length))}
            onClick={() => void send()}
          >
            {busy ? "Sending…" : draft.pending ? "Retry send" : "Send"}
          </button>
        </>
      }
    >
      {error && (
        <p role="alert" className="cm-error">
          {error}
        </p>
      )}
      {draft.pending && !busy && (
        <p role="status">Send status is uncertain. Retry the saved message to confirm delivery.</p>
      )}
      <MessageRecipients
        directory={directory}
        value={draft.recipients}
        onChange={(recipients) => change({ recipients })}
        disabled={locked}
      />
      <section className="cm-context" aria-label="Patient and attached records">
        {draft.patient ? (
          <div>
            <strong>
              {demographics
                ? `${demographics.preferredName || demographics.legalFirstName} ${demographics.legalLastName}`
                : draft.patient.name}
            </strong>
            <small>
              {demographics
                ? `DOB ${demographics.dateOfBirth} · MRN ${demographics.mrn}`
                : draft.patient.identity}
            </small>
            <button
              className="di-button"
              type="button"
              disabled={locked || draft.task}
              onClick={() => change({ patient: null, sources: [] })}
            >
              Remove patient and records
            </button>
          </div>
        ) : (
          <>
            <label>
              Patient · optional
              <input
                value={patientSearch}
                disabled={locked}
                onChange={(e) => setPatientSearch(e.target.value)}
                placeholder="Find a patient by name or MRN"
              />
            </label>
            {patients.isError && <p role="alert">Patient search is unavailable. Try again.</p>}
            {patientSearch.length >= 2 &&
              patients.data?.items.map((p) => (
                <button
                  className="di-button"
                  type="button"
                  key={p.id}
                  disabled={locked}
                  onClick={() =>
                    change({ patient: { id: p.id, name: p.displayName, identity: `MRN ${p.mrn}` } })
                  }
                >
                  {p.displayName} · {p.mrn}
                </button>
              ))}
          </>
        )}
        {draft.sources.map((s, i) => (
          <div key={`${s.kind}:${s.id}`}>
            <span>{s.label}</span>
            <button
              className="di-button"
              type="button"
              disabled={locked || draft.task}
              aria-label={`Remove ${s.label}`}
              onClick={() => change({ sources: draft.sources.filter((_, n) => i !== n) })}
            >
              Remove
            </button>
          </div>
        ))}
      </section>
      <label>
        Subject · optional
        <input
          maxLength={160}
          value={draft.subject}
          disabled={locked}
          onChange={(e) => change({ subject: e.target.value })}
        />
      </label>
      {draft.task && (
        <fieldset disabled={locked}>
          <legend>Staff request</legend>
          <div className="cm-actions">
            {[
              "Please call the patient about this item.",
              "Please book a follow-up appointment.",
              "Please follow up on this item.",
            ].map((text) => (
              <button
                className="di-button"
                type="button"
                key={text}
                onClick={() => change({ body: text })}
              >
                {text.replace("Please ", "").split(" ").slice(0, 3).join(" ")}
              </button>
            ))}
          </div>
          <label>
            Responsible person
            <select
              value={draft.owner}
              onChange={(e) => {
                const owner = e.target.value;
                change({
                  owner,
                  recipients:
                    owner && !draft.recipients.some((r) => r.kind === "user" && r.id === owner)
                      ? [...draft.recipients, { kind: "user", id: owner }]
                      : draft.recipients,
                });
              }}
            >
              <option value="">Shared team queue</option>
              {directory.people.map((p) => (
                <option key={p.userId} value={p.userId}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </label>
          {!draft.owner && <small>Select a team above to receive this request.</small>}
          <label>
            Due · optional
            <input
              type="datetime-local"
              value={draft.due}
              onChange={(e) => change({ due: e.target.value })}
            />
          </label>
        </fieldset>
      )}
      <label>
        Message
        <textarea
          rows={5}
          maxLength={2000}
          value={draft.body}
          disabled={locked}
          onChange={(e) => change({ body: e.target.value })}
        />
      </label>
      <small>Messages are sent only when you choose Send.</small>
    </ClinicMessageWindow>
  );
}

export function ClinicConversationPanel({ id, onClose }: { id: string; onClose(): void }) {
  const { client, directory, siteId, userId, scope } = useClinicMessaging();
  const cache = useQueryClient();
  const key = messageDraftKey(scope, undefined, id);
  const { draft, change } = useDraft(key);
  const detail = useQuery({
    queryKey: ["clinic-message", scope, id],
    queryFn: ({ signal }) => client!.getClinicConversation(siteId, id, signal),
    enabled: Boolean(client),
    refetchInterval: 5000,
    refetchOnWindowFocus: "always",
    retry: false,
  });
  const [older, setOlder] = useState<ClinicConversationDetail["events"]>([]);
  const [before, setBefore] = useState<number | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);
  const [error, setError] = useState("");
  const [manage, setManage] = useState(false);
  const [add, setAdd] = useState<ClinicMessageRecipient[]>([]);
  const [remove, setRemove] = useState<ClinicMessageRecipient[]>([]);
  const [reassign, setReassign] = useState("");
  const [pendingAction, setPendingAction] = useState<ClinicConversationMutation | null>(null);
  const lastRead = useRef(0);
  const c = detail.data?.conversation;
  const loadedSequence = detail.data?.events.at(-1)?.sequence ?? 0;
  const loadedFrom = detail.data?.events[0]?.sequence ?? 1;
  useEffect(() => {
    if (
      !client ||
      !c?.canReply ||
      !c.unread ||
      loadedSequence <= lastRead.current ||
      document.visibilityState === "hidden"
    )
      return;
    let active = true;
    lastRead.current = loadedSequence;
    void client
      .mutateClinicConversation(id, {
        siteId,
        expectedVersion: c.version,
        idempotencyKey: crypto.randomUUID(),
        command: {
          action: "read",
          fromSequence: loadedFrom,
          throughSequence: loadedSequence,
        },
      })
      .then(() => {
        if (active) {
          void cache.invalidateQueries({ queryKey: ["clinic-message-summary"] });
          void cache.invalidateQueries({ queryKey: ["clinic-messages"] });
        }
      })
      .catch(() => {
        lastRead.current = 0;
      });
    return () => {
      active = false;
    };
  }, [client, c?.version, c?.canReply, c?.unread, loadedSequence, loadedFrom, cache, id, siteId]);
  if (!client || !directory) return null;
  const act = async (
    command: ClinicConversationMutation["command"],
    retry?: ClinicConversationMutation,
  ) => {
    if (!c || sending.current) return;
    const payload = retry ?? {
      siteId,
      expectedVersion:
        command.action === "reply" ? (draft.reviewedVersion ?? c.version) : c.version,
      idempotencyKey: crypto.randomUUID(),
      command,
    };
    sending.current = true;
    setBusy(true);
    setError("");
    if (command.action === "reply") change({ pending: payload });
    else setPendingAction(payload);
    try {
      const result = await client.mutateClinicConversation(id, payload);
      cache.setQueryData(["clinic-message", scope, id], result);
      if (command.action === "reply")
        change({ body: "", pending: undefined, reviewedVersion: undefined });
      setPendingAction(null);
      setManage(false);
      setAdd([]);
      setRemove([]);
      refreshMessages(cache);
      if (
        ["done", "reopen", "waiting"].includes(command.action) ||
        (command.action === "participants" &&
          command.remove?.some((p) => p.kind === "user" && p.id === userId))
      )
        onClose();
    } catch (cause) {
      if (cause instanceof ClinicMessagingError && cause.definitive) {
        change({ pending: undefined });
        setPendingAction(null);
        void detail.refetch();
      }
      setError(messageError(cause));
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };
  const locked = busy || Boolean(draft.pending) || Boolean(pendingAction);
  const next = before === undefined ? detail.data?.nextBeforeSequence : before;
  return (
    <ClinicMessageWindow
      title={c?.subject || "Conversation"}
      eyebrow="Attention · Message"
      className="cm-dialog di-dialog"
      onClose={onClose}
      actions={
        c?.canReply && (
          <div className="cm-reply">
            {draft.body &&
              draft.reviewedVersion !== undefined &&
              draft.reviewedVersion !== c.version &&
              !draft.pending && (
                <p role="status">
                  The conversation changed while you were drafting. Review the messages and current
                  participants, then{" "}
                  <button
                    className="di-button"
                    type="button"
                    disabled={locked}
                    onClick={() => change({ reviewedVersion: c.version })}
                  >
                    Use current conversation
                  </button>
                  .
                </p>
              )}
            <label>
              Reply to current participants
              <textarea
                rows={3}
                value={draft.body}
                maxLength={2000}
                disabled={locked}
                onChange={(e) =>
                  change({
                    body: e.target.value,
                    reviewedVersion: draft.reviewedVersion ?? c.version,
                  })
                }
              />
            </label>
            <div className="cm-actions">
              <button
                type="button"
                className="di-button di-primary"
                disabled={
                  busy ||
                  Boolean(pendingAction) ||
                  (!draft.pending &&
                    (!draft.body.trim() ||
                      (draft.reviewedVersion !== undefined && draft.reviewedVersion !== c.version)))
                }
                onClick={() =>
                  void act(
                    { action: "reply", body: draft.body },
                    draft.pending as ClinicConversationMutation | undefined,
                  )
                }
              >
                {busy ? "Saving…" : draft.pending ? "Retry reply" : "Send reply"}
              </button>
              <button
                className="di-button"
                type="button"
                disabled={locked}
                onClick={() => void act({ action: "done", throughSequence: loadedSequence })}
              >
                Done for me
              </button>
              <button
                className="di-button"
                type="button"
                disabled={locked}
                onClick={() =>
                  void act({
                    action: c.bucket === "action" ? "waiting" : "reopen",
                    throughSequence: loadedSequence,
                  })
                }
              >
                {c.bucket === "action" ? "Waiting" : "Needs action"}
              </button>
            </div>
          </div>
        )
      }
    >
      {(error || detail.isError) && (
        <p className="cm-error" role="alert">
          {error || "Connection lost. Your draft is kept; updates will resume when connected."}
        </p>
      )}
      {pendingAction && !busy && (
        <button
          className="di-button"
          type="button"
          onClick={() => void act(pendingAction.command, pendingAction)}
        >
          Retry action
        </button>
      )}
      {!c && (
        <p>{detail.isPending ? "Loading conversation…" : "This conversation is unavailable."}</p>
      )}
      {c && (
        <>
          <div className="cm-context">
            {c.patientId ? (
              <Link
                to="/patients/$patientId"
                params={{ patientId: c.patientId }}
                search={{ section: "overview" }}
              >
                {c.patientName} · Open patient profile
              </Link>
            ) : (
              <strong>Clinic message · No patient attached</strong>
            )}
            {c.sources.map((s) => (
              <div key={`${s.kind}:${s.id}`}>
                <span>{s.label}</span>
                {s.available ? (
                  <MessageSourceLink
                    conversationId={id}
                    source={s}
                    client={client}
                    siteId={siteId}
                  />
                ) : (
                  <small>Unavailable or superseded</small>
                )}
              </div>
            ))}
          </div>
          {c.historyOnly && (
            <p role="status">
              Read-only history. Chart access does not subscribe you to this conversation.
            </p>
          )}
          <p className="cm-participant-summary">
            {c.participants
              .filter((p) => p.active)
              .map((p) => p.name)
              .join(" · ")}
          </p>
          {c.canManage && (
            <div className="cm-actions">
              <button
                className="di-button"
                type="button"
                disabled={locked}
                onClick={() => setManage(!manage)}
              >
                Add people / manage participants
              </button>
              <button
                className="di-button"
                type="button"
                disabled={locked}
                onClick={() => {
                  setManage(true);
                  setRemove([{ kind: "user", id: userId }]);
                }}
              >
                Leave conversation
              </button>
            </div>
          )}
          {manage && (
            <section className="cm-management">
              <MessageRecipients
                directory={directory}
                value={add}
                onChange={setAdd}
                disabled={locked}
              />
              <fieldset disabled={locked}>
                <legend>Remove participants</legend>
                {[
                  ...c.people.filter((p) => p.active).map((p) => ({ ...p, kind: "user" as const })),
                  ...c.participants.filter((p) => p.active && p.kind === "team"),
                ].map((p) => (
                  <label key={`${p.kind}:${p.id}`}>
                    <input
                      type="checkbox"
                      checked={remove.some((r) => r.kind === p.kind && r.id === p.id)}
                      onChange={(e) =>
                        setRemove(
                          e.target.checked
                            ? [...remove, { kind: p.kind, id: p.id }]
                            : remove.filter((r) => !(r.kind === p.kind && r.id === p.id)),
                        )
                      }
                    />
                    {p.name}
                  </label>
                ))}
              </fieldset>
              {c.request && (
                <label>
                  Reassign outstanding request to
                  <select value={reassign} onChange={(e) => setReassign(e.target.value)}>
                    <option value="">Keep owner / return to active team</option>
                    {directory.people
                      .filter((p) => !remove.some((r) => r.kind === "user" && r.id === p.userId))
                      .map((p) => (
                        <option key={p.userId} value={p.userId}>
                          {p.displayName}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              <p>
                New participants receive existing history. Removed people stop receiving future
                replies.
              </p>
              <button
                className="di-button"
                type="button"
                disabled={locked || (!add.length && !remove.length)}
                onClick={() =>
                  void act({
                    action: "participants",
                    add:
                      reassign && !add.some((p) => p.kind === "user" && p.id === reassign)
                        ? [...add, { kind: "user", id: reassign }]
                        : add,
                    remove,
                    ...(reassign ? { reassignTo: reassign } : {}),
                  })
                }
              >
                Apply participant changes
              </button>
            </section>
          )}
          {c.request && (
            <section className="cm-work">
              <strong>Staff request · {c.request.status.replaceAll("_", " ")}</strong>
              <p>
                {c.request.assignedToName ?? "Shared team queue"}
                {c.request.dueAt ? ` · Due ${new Date(c.request.dueAt).toLocaleString()}` : ""}
              </p>
              {c.needsReassignment && (
                <p role="alert">Responsible person is unavailable. Reassign this request.</p>
              )}
              {c.canManage && ["open", "in_progress"].includes(c.request.status) && (
                <label>
                  Assign request
                  <select
                    value={c.request.assignedTo ?? ""}
                    disabled={locked}
                    onChange={(e) => {
                      if (e.target.value) void act({ action: "reassign", userId: e.target.value });
                    }}
                  >
                    <option value="">Team queue</option>
                    {c.people
                      .filter((p) => p.active)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </select>
                </label>
              )}
              {c.canReply &&
                c.request.assignedTo === userId &&
                ["open", "in_progress"].includes(c.request.status) && (
                  <button
                    className="di-button"
                    type="button"
                    disabled={locked}
                    onClick={() => void act({ action: "complete_request" })}
                  >
                    Complete request
                  </button>
                )}
            </section>
          )}
          {!c.request && c.needsReassignment && c.canManage && (
            <label>
              Reassign outstanding message
              <select
                disabled={locked}
                value=""
                onChange={(e) => {
                  if (e.target.value) void act({ action: "reassign", userId: e.target.value });
                }}
              >
                <option value="">Choose a current participant</option>
                {c.people
                  .filter((p) => p.active)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </select>
            </label>
          )}
          {c.teams.map((t) => (
            <section className="cm-work" key={t.id}>
              <strong>{t.name}</strong>
              <span>
                {t.ownerName ? ` · ${t.ownerName}` : " · Unclaimed"} ·{" "}
                {t.status.replaceAll("_", " ")}
              </span>
              {t.canClaim && (
                <button
                  className="di-button"
                  type="button"
                  disabled={locked}
                  onClick={() => void act({ action: "claim", teamId: t.id })}
                >
                  Pick up team message
                </button>
              )}
              {t.canComplete && (
                <>
                  <button
                    className="di-button"
                    type="button"
                    disabled={locked}
                    onClick={() => void act({ action: "team_done", teamId: t.id })}
                  >
                    Mark team done
                  </button>
                  <button
                    className="di-button"
                    type="button"
                    disabled={locked}
                    onClick={() => void act({ action: "release", teamId: t.id })}
                  >
                    Return to team
                  </button>
                </>
              )}
            </section>
          ))}
          {next && (
            <button
              className="di-button"
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const page = await client.getClinicConversation(siteId, id, undefined, next);
                  setOlder((old) => [...page.events, ...old]);
                  setBefore(page.nextBeforeSequence);
                  const first = page.events[0]?.sequence;
                  const last = page.events.at(-1)?.sequence;
                  if (page.conversation.canReply && first && last) {
                    await client.mutateClinicConversation(id, {
                      siteId,
                      expectedVersion: page.conversation.version,
                      idempotencyKey: crypto.randomUUID(),
                      command: { action: "read", fromSequence: first, throughSequence: last },
                    });
                    refreshMessages(cache);
                  }
                } catch (cause) {
                  setError(messageError(cause));
                } finally {
                  setBusy(false);
                }
              }}
            >
              Load earlier messages
            </button>
          )}
          <ol className="cm-timeline" aria-label="Conversation history">
            {[...older, ...(detail.data?.events ?? [])]
              .filter((e, i, all) => all.findIndex((v) => v.sequence === e.sequence) === i)
              .map((e) => (
                <li
                  key={e.sequence}
                  className={`cm-event cm-event-${e.kind}${e.kind === "message" && e.authorId === userId ? " cm-event-own" : ""}`}
                >
                  <div>
                    <strong>{e.authorName}</strong>
                    <time dateTime={e.createdAt}>{new Date(e.createdAt).toLocaleString()}</time>
                  </div>
                  <p>{e.body}</p>
                </li>
              ))}
          </ol>
        </>
      )}
    </ClinicMessageWindow>
  );
}

function MessageSourceLink({
  conversationId,
  source,
  client,
  siteId,
}: {
  conversationId: string;
  source: ClinicConversation["sources"][number];
  client: MessageClient;
  siteId: string;
}) {
  const [content, setContent] = useState<{ title: string; text: string } | null>(null);
  const [file, setFile] = useState<Uint8Array | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  useEffect(
    () => () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    },
    [imageUrl],
  );
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <>
      <button
        className="di-button"
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError("");
          setFile(null);
          setImageUrl(null);
          try {
            const value = await client.getClinicConversationSource(
              siteId,
              conversationId,
              source.kind,
              source.id,
            );
            if (value.hasFile) {
              const blob = await client.getClinicConversationFile(
                siteId,
                conversationId,
                source.kind,
                source.id,
              );
              if (blob.type === "application/pdf")
                setFile(new Uint8Array(await blob.arrayBuffer()));
              else if (["image/png", "image/jpeg"].includes(blob.type))
                setImageUrl(URL.createObjectURL(blob));
            }
            setContent(value);
          } catch (cause) {
            setError(messageError(cause));
          } finally {
            setBusy(false);
          }
        }}
      >
        {busy ? "Opening…" : "Open attached record"}
      </button>
      {error && <small role="alert">{error}</small>}
      {content && (
        <ClinicMessageWindow
          title={content.title}
          className="cm-dialog di-dialog"
          onClose={() => {
            setContent(null);
            setFile(null);
            setImageUrl(null);
          }}
        >
          {file ? (
            <Suspense fallback={<p>Loading original…</p>}>
              <StoredPdfPreview bytes={file} fileName="attached-record.pdf" label={content.title} />
            </Suspense>
          ) : imageUrl ? (
            <img style={{ maxWidth: "100%" }} src={imageUrl} alt={content.title} />
          ) : (
            <pre className="cm-source-content">{content.text}</pre>
          )}
        </ClinicMessageWindow>
      )}
    </>
  );
}

export function PatientMessages({ patientId }: { patientId: string }) {
  const messaging = useClinicMessaging();
  const [expanded, setExpanded] = useState(false);
  const list = useInfiniteQuery({
    queryKey: ["patient-messages", messaging.scope, patientId],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      messaging.client!.listPatientConversations(messaging.siteId, patientId, pageParam, signal),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: expanded && messaging.enabled,
    refetchInterval: 5000,
    refetchOnWindowFocus: "always",
    retry: false,
  });
  if (!messaging.enabled) return null;
  return (
    <section className="pp-panel cm-patient-history">
      <button
        className="pp-record-toggle"
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <strong>Messages</strong>
        <span>{expanded ? "Hide" : "Show"}</span>
      </button>
      {expanded && (
        <>
          {list.isPending && <p>Loading messages…</p>}
          {list.isError && <p role="alert">Messages could not be refreshed.</p>}
          {list.data?.pages
            .flatMap((p) => p.items)
            .map((c) => (
              <button
                type="button"
                className="cm-history-row"
                key={c.id}
                onClick={() => messaging.open(c.id)}
              >
                <strong>{c.subject}</strong>
                <span>{c.preview}</span>
                <small>{new Date(c.updatedAt).toLocaleString()}</small>
              </button>
            ))}
          {list.data?.pages[0]?.items.length === 0 && <p>No messages about this patient.</p>}
          {list.hasNextPage && (
            <button
              className="di-button"
              type="button"
              onClick={() => void list.fetchNextPage()}
              disabled={list.isFetchingNextPage}
            >
              More messages
            </button>
          )}
        </>
      )}
    </section>
  );
}
